import { PublicClient } from "./public.js";
export class AdminClient extends PublicClient {
    async listSites() {
        const res = await this.request("GET", "/v1/admin/sites", undefined, true);
        return res.sites;
    }
    /** Public marketing cards for lumorasites.com. No auth. */
    async listFeaturedWork() {
        const res = await this.request("GET", "/v1/featured-work");
        return res.items ?? [];
    }
    async adminCreateOwner(slug, input) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/owners`, input, true);
    }
    /** Admin: list RoleOwner accounts for a site. */
    async listSiteOwners(slug) {
        const res = await this.request("GET", `/v1/admin/sites/${encodeURIComponent(slug)}/owners`, undefined, true);
        return res.owners;
    }
    /** Admin: list a site's sales leads, newest first (limit default 50, max 200). */
    async listSiteLeads(slug, opts = {}) {
        const q = new URLSearchParams();
        if (opts.limit !== undefined)
            q.set("limit", String(opts.limit));
        if (opts.offset !== undefined)
            q.set("offset", String(opts.offset));
        const qs = q.toString();
        const res = await this.request("GET", `/v1/admin/sites/${encodeURIComponent(slug)}/leads${qs ? `?${qs}` : ""}`, undefined, true);
        return res.leads ?? [];
    }
    /** Admin: list every account on a site (primary owner flagged). */
    async listSiteUsers(slug) {
        const res = await this.request("GET", `/v1/admin/sites/${encodeURIComponent(slug)}/users`, undefined, true);
        return res.users ?? [];
    }
    /** Admin: create a staff account on a site. */
    async adminCreateSiteUser(slug, input) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/users`, input, true);
    }
    /** Admin: change a site account's role. */
    async adminPatchSiteUser(slug, id, input) {
        return this.request("PATCH", `/v1/admin/sites/${encodeURIComponent(slug)}/users/${encodeURIComponent(id)}`, input, true);
    }
    /** Admin: email a password-reset link to one site account. */
    async adminRequestUserPasswordReset(slug, id, input) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/users/${encodeURIComponent(id)}/password-reset`, input, true);
    }
    /** Admin: email secure password-reset link to the existing site owner. */
    async adminRequestOwnerPasswordReset(slug, input) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/owners/password-reset`, input, true);
    }
    /** Admin: create one-time owner invite URL (emails when SMTP configured). */
    async adminCreateOwnerInvite(slug, input) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/owner-invites`, input, true);
    }
    /** Admin: record an offline/cash payment → activate site + optional hosting waive. */
    async recordPayment(slug, input) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/payments`, input, true);
    }
    /** Admin: list offline payments for a site. */
    async listPayments(slug) {
        const res = await this.request("GET", `/v1/admin/sites/${encodeURIComponent(slug)}/payments`, undefined, true);
        return res.payments;
    }
    /** Admin: platform-wide payments ledger (Stripe + offline) with totals. */
    async listAllPayments(params) {
        const q = new URLSearchParams();
        if (params?.from)
            q.set("from", params.from);
        if (params?.to)
            q.set("to", params.to);
        if (params?.source)
            q.set("source", params.source);
        if (params?.limit)
            q.set("limit", String(params.limit));
        return this.request("GET", `/v1/admin/payments?${q.toString()}`, undefined, true);
    }
    /** Admin: import Stripe invoice history into the payments ledger. */
    async syncStripePayments() {
        return this.request("POST", "/v1/admin/payments/sync-stripe", undefined, true);
    }
    async listAdminEvents(limit = 100, opts) {
        let path = `/v1/admin/events?limit=${limit}`;
        if (opts?.tenant) {
            path += `&tenant=${encodeURIComponent(opts.tenant)}`;
        }
        const res = await this.request("GET", path, undefined, true);
        return res.events;
    }
    async getPlatformUsage() {
        return this.request("GET", "/v1/admin/usage", undefined, true);
    }
    /** Admin: one site's current-period usage (includes byDay for charts). */
    async getAdminSiteUsage(slug) {
        return this.request("GET", `/v1/admin/sites/${encodeURIComponent(slug)}/usage`, undefined, true);
    }
    /** Platform admin: every runtime config entry with value, default and metadata. */
    async listPlatformConfig() {
        const res = await this.request("GET", "/v1/admin/config", undefined, true);
        return res.entries ?? [];
    }
    /** Platform admin: set one config key. 400 validation_error on bad value, 404 unknown key. */
    async setPlatformConfig(key, value) {
        return this.request("PUT", `/v1/admin/config/${encodeURIComponent(key)}`, { value }, true);
    }
    /** Platform admin: change history for a config key, newest first. */
    async getPlatformConfigHistory(key, limit) {
        const q = limit !== undefined ? `?limit=${encodeURIComponent(String(limit))}` : "";
        const res = await this.request("GET", `/v1/admin/config/${encodeURIComponent(key)}/history${q}`, undefined, true);
        return res.history ?? [];
    }
    /** Admin: a site's storage quota and live usage. */
    async getSiteStorageQuota(slug) {
        return this.request("GET", `/v1/admin/sites/${encodeURIComponent(slug)}/storage-quota`, undefined, true);
    }
    /** Admin: per-tenant ledger rows for a UTC `YYYY-MM` period (default: current). */
    async getAdminBillingUsage(period) {
        const qs = period ? `?period=${encodeURIComponent(period)}` : "";
        return this.request("GET", `/v1/admin/billing/usage${qs}`, undefined, true);
    }
    /** Admin: close a billing month; `dryRun` returns computed lines and creates nothing. */
    async closeBillingMonth(period, dryRun) {
        return this.request("POST", `/v1/admin/billing/close?period=${encodeURIComponent(period)}&dryRun=${dryRun ? "true" : "false"}`, undefined, true);
    }
    /** Admin: mark/unmark a site as founder (never billed, full Pro). */
    async setSiteFounder(slug, founder) {
        return this.request("PUT", `/v1/admin/sites/${encodeURIComponent(slug)}/founder`, { founder }, true);
    }
    /** Admin: set a site's storage quota and overage policy. */
    async setSiteStorageQuota(slug, input) {
        return this.request("PUT", `/v1/admin/sites/${encodeURIComponent(slug)}/storage-quota`, input, true);
    }
    async listSiteOrders(slug) {
        const res = await this.request("GET", `/v1/admin/sites/${encodeURIComponent(slug)}/orders`, undefined, true);
        return res.orders;
    }
    async listEdgeDevices(slug) {
        const res = await this.request("GET", `/v1/admin/sites/${encodeURIComponent(slug)}/edge-devices`, undefined, true);
        return res.devices;
    }
    async provisionEdgeDevice(slug, name, opts) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/edge-devices`, { name, sandbox: opts?.sandbox === true, locationId: opts?.locationId ?? "" }, true);
    }
    /** Owner: bind a trunk-stock Pi 6-digit pairing code to this site (#888). */
    async claimEdgeDevice(slug, input) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/edge-devices/claim`, {
            code: input.code,
            name: input.name ?? "",
            locationId: input.locationId ?? "",
            ...(input.role ? { role: input.role } : {}),
        }, true);
    }
    /** Owner/manager: unpair (revoke) a Dine screen of this site (#1100). Printers stay admin-only. */
    async unpairEdgeDevice(slug, id) {
        await this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/edge-devices/${encodeURIComponent(id)}/revoke`, undefined, true);
    }
    /** Platform admin test order. target real = kitchen pool, sandbox = pinned device. No card. */
    async placeTestOrder(slug, target) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/orders/test`, { target }, true);
    }
    /**
     * Site-owner test order (#750). Tenant is the JWT only — the body is the
     * printer target, never a slug. Same PlaceTestOrder command as placeTestOrder.
     */
    async placeMyTestOrder(target) {
        return this.request("POST", "/v1/orders/test", { target }, true);
    }
    async deleteEdgeDevice(slug, id) {
        await this.request("DELETE", `/v1/admin/sites/${encodeURIComponent(slug)}/edge-devices/${encodeURIComponent(id)}`, undefined, true);
    }
    /** Admin: revoke a kitchen edge-device token (204, no body). */
    async revokeEdgeDevice(slug, id) {
        await this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/edge-devices/${encodeURIComponent(id)}/revoke`, undefined, true);
    }
    /** Admin: rotate a kitchen edge-device token (token shown once). */
    async rotateEdgeDevice(slug, id) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/edge-devices/${encodeURIComponent(id)}/rotate`, undefined, true);
    }
    /** Admin: enqueue a command for a kitchen edge device. */
    async enqueueEdgeCommand(slug, id, input) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/edge-devices/${encodeURIComponent(id)}/commands`, input, true);
    }
    /** Admin: put a kitchen print job back on the poll queue. */
    async requeueOrderPrint(slug, id) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/orders/${encodeURIComponent(id)}/reprint`, undefined, true);
    }
    /** Admin: recent marketing QR slip print jobs for a site, newest first (#1139). */
    async listSlipPrintJobs(slug) {
        const res = await this.request("GET", `/v1/admin/sites/${encodeURIComponent(slug)}/slip-print-jobs`, undefined, true);
        return res.jobs;
    }
    /** Admin: queue N copies of the QR slip on the site's kitchen Pi; no SSH (#1139). */
    async createSlipPrintJob(slug, input) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/slip-print-jobs`, { copies: input.copies, title: input.title ?? "", qrUrl: input.qrUrl ?? "" }, true);
    }
    /** Admin: stop a queued or printing slip job after the current copy. */
    async cancelSlipPrintJob(slug, id) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/slip-print-jobs/${encodeURIComponent(id)}/cancel`, undefined, true);
    }
    /** Admin: upsert a tenant's non-secret Stripe billing config. */
    async setBillingConfig(slug, input) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/billing-config`, input, true);
    }
    /** Admin: write service tier, hosting plan, and add-ons for a tenant. */
    async putSiteEntitlement(slug, input) {
        return this.request("PUT", `/v1/admin/sites/${encodeURIComponent(slug)}/entitlement`, input, true);
    }
    /** Admin: list platform Stripe price catalog rows. */
    async listPlatformStripePrices() {
        const res = await this.request("GET", "/v1/admin/platform/stripe-prices", undefined, true);
        return res.prices;
    }
    /** Admin: upsert one platform Stripe price catalog row. */
    async setPlatformStripePrice(key, input) {
        return this.request("PUT", `/v1/admin/platform/stripe-prices/${encodeURIComponent(key)}`, input, true);
    }
    /** Opens a support ticket for the signed-in tenant. */
    async createTicket(input) {
        return this.request("POST", "/v1/tickets", input, true);
    }
    /** Lists the signed-in tenant's support tickets. */
    async listTickets() {
        const res = await this.request("GET", "/v1/tickets", undefined, true);
        return res.tickets;
    }
}
//# sourceMappingURL=admin.js.map